// Messaging System Integration Utilities
// Provides helper functions to send alerts from various restaurant workflows

import { API_ENDPOINTS } from '../config/apiRouting';

export interface AlertMetadata {
  tableNumber?: string;
  orderNumber?: string;
  itemName?: string;
  employeeId?: string;
  employeeName?: string;
  amount?: number;
  customerId?: string;
  requestId?: string;
  approvalType?: 'void' | 'discount' | 'refund';
  [key: string]: any;
}

export interface AlertOptions {
  storeId: string;
  senderId?: string;
  senderName?: string;
  specificUsers?: string[];
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: AlertMetadata;
}

class MessagingIntegration {
  private static instance: MessagingIntegration;
  private baseUrl: string;

  private constructor() {
    this.baseUrl = API_ENDPOINTS.messaging?.url || '';
  }

  public static getInstance(): MessagingIntegration {
    if (!MessagingIntegration.instance) {
      MessagingIntegration.instance = new MessagingIntegration();
    }
    return MessagingIntegration.instance;
  }

  /**
   * Send KDS ready alert to assigned server
   */
  async sendKDSReadyAlert(options: AlertOptions & { 
    tableNumber: string;
    orderNumber: string;
    assignedServerId: string;
    assignedServerName: string;
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: 'KDS_READY',
      message: `Table ${options.tableNumber} order is ready for pickup`,
      senderId: options.senderId || 'kitchen-system',
      senderName: options.senderName || 'Kitchen',
      specificUsers: [options.assignedServerId],
      priority: 'high',
      metadata: {
        tableNumber: options.tableNumber,
        orderNumber: options.orderNumber,
        assignedServerId: options.assignedServerId,
        assignedServerName: options.assignedServerName,
        alertType: 'kds_ready'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send table seated alert to assigned server
   */
  async sendTableSeatedAlert(options: AlertOptions & {
    tableNumber: string;
    partySize: number;
    assignedServerId: string;
    assignedServerName: string;
    hostessId?: string;
    hostessName?: string;
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: 'TABLE_SEATED',
      message: `Table ${options.tableNumber} has been seated (party of ${options.partySize})`,
      senderId: options.hostessId || options.senderId || 'hostess-system',
      senderName: options.hostessName || options.senderName || 'Hostess',
      specificUsers: [options.assignedServerId],
      priority: 'medium',
      metadata: {
        tableNumber: options.tableNumber,
        partySize: options.partySize,
        assignedServerId: options.assignedServerId,
        assignedServerName: options.assignedServerName,
        hostessId: options.hostessId,
        hostessName: options.hostessName,
        alertType: 'table_seated'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send item 86'd alert to all relevant staff
   */
  async sendItem86Alert(options: AlertOptions & {
    itemName: string;
    itemId: string;
    reason?: string;
    managerId?: string;
    managerName?: string;
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: 'ITEM_86',
      message: `${options.itemName} is now 86'd (unavailable)${options.reason ? `: ${options.reason}` : ''}`,
      senderId: options.managerId || options.senderId || 'system',
      senderName: options.managerName || options.senderName || 'Manager',
      priority: 'high',
      metadata: {
        itemName: options.itemName,
        itemId: options.itemId,
        reason: options.reason,
        managerId: options.managerId,
        managerName: options.managerName,
        alertType: 'item_86'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send online order alert to managers
   */
  async sendOnlineOrderAlert(options: AlertOptions & {
    orderNumber: string;
    customerName: string;
    orderTotal: number;
    estimatedTime?: number;
    orderType: 'pickup' | 'delivery';
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: 'ONLINE_ORDER',
      message: `New ${options.orderType} order #${options.orderNumber} from ${options.customerName} ($${options.orderTotal.toFixed(2)})`,
      senderId: options.senderId || 'online-system',
      senderName: options.senderName || 'Online Orders',
      priority: 'high',
      metadata: {
        orderNumber: options.orderNumber,
        customerName: options.customerName,
        orderTotal: options.orderTotal,
        estimatedTime: options.estimatedTime,
        orderType: options.orderType,
        alertType: 'online_order'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send approval request to managers
   */
  async sendApprovalRequest(options: AlertOptions & {
    requestId: string;
    approvalType: 'void' | 'discount' | 'refund';
    amount: number;
    reason: string;
    requestingEmployeeId: string;
    requestingEmployeeName: string;
    tableNumber?: string;
    orderNumber?: string;
  }) {
    const typeLabels = {
      void: 'Void',
      discount: 'Discount',
      refund: 'Refund'
    };

    const alertData = {
      storeId: options.storeId,
      alertType: 'APPROVAL_REQUEST',
      message: `${typeLabels[options.approvalType]} approval needed: $${options.amount.toFixed(2)} - ${options.reason}`,
      senderId: options.requestingEmployeeId,
      senderName: options.requestingEmployeeName,
      priority: 'urgent',
      metadata: {
        requestId: options.requestId,
        approvalType: options.approvalType,
        amount: options.amount,
        reason: options.reason,
        requestingEmployeeId: options.requestingEmployeeId,
        requestingEmployeeName: options.requestingEmployeeName,
        tableNumber: options.tableNumber,
        orderNumber: options.orderNumber,
        alertType: 'approval_request'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send approval response to requesting employee
   */
  async sendApprovalResponse(options: AlertOptions & {
    requestId: string;
    approved: boolean;
    approvalType: 'void' | 'discount' | 'refund';
    amount: number;
    managerId: string;
    managerName: string;
    requestingEmployeeId: string;
    reason?: string;
  }) {
    const typeLabels = {
      void: 'Void',
      discount: 'Discount',
      refund: 'Refund'
    };

    const status = options.approved ? 'approved' : 'denied';
    const message = options.approved
      ? `${typeLabels[options.approvalType]} of $${options.amount.toFixed(2)} has been approved`
      : `${typeLabels[options.approvalType]} of $${options.amount.toFixed(2)} has been denied${options.reason ? `: ${options.reason}` : ''}`;

    const alertData = {
      storeId: options.storeId,
      alertType: 'APPROVAL_RESPONSE',
      message,
      senderId: options.managerId,
      senderName: options.managerName,
      specificUsers: [options.requestingEmployeeId],
      priority: 'high',
      metadata: {
        requestId: options.requestId,
        approved: options.approved,
        approvalType: options.approvalType,
        amount: options.amount,
        managerId: options.managerId,
        managerName: options.managerName,
        requestingEmployeeId: options.requestingEmployeeId,
        reason: options.reason,
        status,
        alertType: 'approval_response'
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send custom alert
   */
  async sendCustomAlert(options: AlertOptions & {
    alertType: string;
    message: string;
    targetRoles?: string[];
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: options.alertType.toUpperCase(),
      message: options.message,
      senderId: options.senderId || 'system',
      senderName: options.senderName || 'System',
      specificUsers: options.specificUsers,
      targetRoles: options.targetRoles,
      priority: options.priority || 'medium',
      metadata: {
        ...options.metadata,
        alertType: options.alertType.toLowerCase()
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Send broadcast message (for managers)
   */
  async sendBroadcastMessage(options: AlertOptions & {
    message: string;
    targetRoles?: string[];
  }) {
    const alertData = {
      storeId: options.storeId,
      alertType: 'BROADCAST',
      message: options.message,
      senderId: options.senderId || 'system',
      senderName: options.senderName || 'Manager',
      targetRoles: options.targetRoles || ['all'],
      priority: options.priority || 'medium',
      metadata: {
        broadcastType: 'manual',
        ...options.metadata
      }
    };

    return this.sendAlert(alertData);
  }

  /**
   * Core alert sending function
   */
  private async sendAlert(alertData: any) {
    try {
      if (!this.baseUrl) {
        console.warn('Messaging service URL not configured');
        return { success: false, error: 'Service not configured' };
      }

      const response = await fetch(`${this.baseUrl}/api/alerts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('Alert sent successfully:', result);
      return { success: true, ...result };

    } catch (error) {
      console.error('Failed to send alert:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get alert history
   */
  async getAlertHistory(storeId: string, limit = 100) {
    try {
      if (!this.baseUrl) {
        throw new Error('Messaging service URL not configured');
      }

      const response = await fetch(`${this.baseUrl}/api/messages/history/${storeId}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.messages || [];

    } catch (error) {
      console.error('Failed to get alert history:', error);
      return [];
    }
  }

  /**
   * Mark alert as read
   */
  async markAlertAsRead(messageId: string, userId: string) {
    try {
      if (!this.baseUrl) {
        return false;
      }

      const response = await fetch(`${this.baseUrl}/api/messages/${messageId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      return response.ok;

    } catch (error) {
      console.error('Failed to mark alert as read:', error);
      return false;
    }
  }
}

// Singleton instance
export const messagingService = MessagingIntegration.getInstance();

// Convenience functions for common use cases
export const sendKDSReady = (options: Parameters<MessagingIntegration['sendKDSReadyAlert']>[0]) =>
  messagingService.sendKDSReadyAlert(options);

export const sendTableSeated = (options: Parameters<MessagingIntegration['sendTableSeatedAlert']>[0]) =>
  messagingService.sendTableSeatedAlert(options);

export const sendItem86 = (options: Parameters<MessagingIntegration['sendItem86Alert']>[0]) =>
  messagingService.sendItem86Alert(options);

export const sendOnlineOrder = (options: Parameters<MessagingIntegration['sendOnlineOrderAlert']>[0]) =>
  messagingService.sendOnlineOrderAlert(options);

export const sendApprovalRequest = (options: Parameters<MessagingIntegration['sendApprovalRequest']>[0]) =>
  messagingService.sendApprovalRequest(options);

export const sendApprovalResponse = (options: Parameters<MessagingIntegration['sendApprovalResponse']>[0]) =>
  messagingService.sendApprovalResponse(options);

export const sendCustomAlert = (options: Parameters<MessagingIntegration['sendCustomAlert']>[0]) =>
  messagingService.sendCustomAlert(options);

export const sendBroadcast = (options: Parameters<MessagingIntegration['sendBroadcastMessage']>[0]) =>
  messagingService.sendBroadcastMessage(options);

export default messagingService;